import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  MessageCircle, Send, AlertCircle, Clock, CheckCircle2,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { formatDateEST, formatDateTimeEST } from '../../utils/timezone';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ClientSupportTab({ clientId, isAdmin = true }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, [clientId]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const endpoint = isAdmin 
        ? `${API}/api/admin/support/tickets`
        : `${API}/api/staff/support/tickets`;
      
      const response = await fetch(endpoint, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Filter tickets for this client
        const clientTickets = data.tickets.filter(t => t.client_id === clientId);
        setTickets(clientTickets);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (ticketId) => {
    if (!replyText.trim()) return;

    setSendingReply(true);

    try {
      const endpoint = isAdmin 
        ? `${API}/api/admin/support/reply/${ticketId}`
        : `${API}/api/staff/support/reply/${ticketId}`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: replyText })
      });

      if (response.ok) {
        setReplyText('');
        await fetchTickets();
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      const endpoint = isAdmin 
        ? `${API}/api/admin/support/update/${ticketId}`
        : `${API}/api/staff/support/update/${ticketId}`;

      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        await fetchTickets();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'In Progress':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'Resolved':
      case 'Closed':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      default:
        return <MessageCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'High':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Low':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-gray-500">Loading support tickets...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          Support Tickets ({tickets.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {tickets.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No support tickets yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => {
              const isExpanded = expandedTicket === ticket.ticket_id;
              
              return (
                <div
                  key={ticket.ticket_id}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Ticket Header */}
                  <div
                    onClick={() => setExpandedTicket(isExpanded ? null : ticket.ticket_id)}
                    className="p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusIcon(ticket.status)}
                          <h4 className="font-semibold text-gray-900">{ticket.subject}</h4>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs">
                          <span className={`px-2 py-1 rounded-full border ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority}
                          </span>
                          <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-300">
                            {ticket.status}
                          </span>
                          <span className="text-gray-500">
                            Created: {formatDateEST(ticket.created_at)}
                          </span>
                          {ticket.replies && ticket.replies.length > 0 && (
                            <span className="text-gray-500">
                              {ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost">
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Ticket Details */}
                  {isExpanded && (
                    <div className="p-4 space-y-4 bg-white">
                      {/* Description */}
                      <div className="pb-4 border-b">
                        <p className="text-sm text-gray-700">{ticket.description}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          Created by: {ticket.created_by}
                        </p>
                      </div>

                      {/* Status Controls */}
                      <div className="flex items-center gap-3 pb-4 border-b">
                        <label className="text-sm font-medium text-gray-700">
                          Status:
                        </label>
                        <Select
                          value={ticket.status}
                          onValueChange={(value) => handleUpdateStatus(ticket.ticket_id, value)}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Open">Open</SelectItem>
                            <SelectItem value="In Progress">In Progress</SelectItem>
                            <SelectItem value="Resolved">Resolved</SelectItem>
                            <SelectItem value="Closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Replies Thread */}
                      {ticket.replies && ticket.replies.length > 0 && (
                        <div className="space-y-3">
                          <h5 className="font-medium text-sm text-gray-900">Conversation</h5>
                          {ticket.replies.map((reply) => (
                            <div
                              key={reply.reply_id}
                              className={`p-3 rounded-lg ${
                                reply.sender_type === 'client'
                                  ? 'bg-blue-50 border border-blue-200'
                                  : 'bg-purple-50 border border-purple-200'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-gray-900">
                                  {reply.sender_name}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatDateTimeEST(reply.timestamp)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">{reply.message}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Reply Form */}
                      {ticket.status !== 'Closed' && (
                        <div className="space-y-3 pt-4 border-t">
                          <Textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Type your reply..."
                            rows={3}
                            className="w-full"
                          />
                          <Button
                            onClick={() => handleSendReply(ticket.ticket_id)}
                            disabled={sendingReply || !replyText.trim()}
                            size="sm"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            {sendingReply ? 'Sending...' : 'Send Reply'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
